/**
 * Shared MasterPower presence logic, used by the admin-only "Controller hardware"
 * card in the status detail drawer (the cruscotto no longer shows the board).
 *
 * MasterPower is only relevant for the full setup driving an external (DSLR)
 * camera. In that scenario it is expected to be present, so a missing or
 * disconnected unit is an actual problem (red), not a neutral state. Any other
 * setup, or internal-camera mode, renders as the neutral "not in use" state —
 * even if a stale masterPower.configured lingers from a previous pairing,
 * because changing setup in the app does not clear that field. Gating on
 * setup.config + camera mode avoids false "disconnected" alarms when
 * MasterPower is not part of the active configuration.
 *
 * setup.config may legitimately be absent: the unauthenticated Android write
 * allowlist in firestore.rules has no clause for `setup`, so the app's
 * reconcile/infer back-fill is rejected and the field stays undefined on every
 * view paired that way. Treating undefined as "not full setup" hid a genuinely
 * connected relay behind the neutral state. When the config is unknown, fall
 * back to the relay's own pairing flag — a configured MasterPower only exists
 * in a full setup — so presence is reported from the evidence we do have.
 */

import type { CameraMode, View } from '@/types';
import type { StatusType } from './StatusDashboardIcon';

export type MasterPowerStatus = 'connected' | 'disconnected' | 'not_paired' | 'not_in_use';

export type SetupConfig = 'camera_only' | 'hub_camera' | 'full_setup';

export function getMasterPowerStatus(
  mp: View['masterPower'],
  setupConfig?: SetupConfig,
  cameraMode?: CameraMode
): MasterPowerStatus {
  // Only relevant when running the external (DSLR) camera.
  if (cameraMode !== 'dslr') return 'not_in_use';
  // An explicit non-full setup rules MasterPower out. An absent setup.config is
  // unknown rather than negative (see above), so defer to the relay's own
  // pairing flag instead of assuming it is not in use.
  if (setupConfig !== undefined && setupConfig !== 'full_setup') return 'not_in_use';
  // Enabled but no relay has ever been paired. Distinct from 'disconnected': nothing is
  // broken, the setup is simply incomplete, and it is fixed by pairing from the app rather
  // than by checking power or range. Also covers a relay that was unpaired later on, which
  // silently disables DSLR recovery — worth surfacing rather than hiding behind a generic
  // offline state.
  if (!mp?.configured) return setupConfig === 'full_setup' ? 'not_paired' : 'not_in_use';
  return mp.connected ? 'connected' : 'disconnected';
}

/** Maps MasterPower presence to the status-icon color (green / red / grey). */
export const MASTER_POWER_ICON_STATUS: Record<MasterPowerStatus, StatusType> = {
  connected: 'success',
  disconnected: 'error',
  not_paired: 'warning',
  not_in_use: 'default',
};

/** Short label shown in the drawer card value line. */
export const MASTER_POWER_LABEL: Record<MasterPowerStatus, string> = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  not_paired: 'Not paired',
  not_in_use: 'Not in use',
};
